// Keyboard + mouse (pointer lock) and gamepads (standard mapping). Look is applied every
// render frame; buttons are latched so a click between two ticks is never lost.

import { Btn } from '../../sim/player';
import { firstPad } from './padnav';

export interface Settings {
  sensitivity: number; // mouse, radians per pixel * 1000
  padSensitivity: number;
  invertY: boolean;
  volume: number;
  /** music level 0..1, rumble 0..1, screen shake 0..1 (pause menu) */
  music?: number;
  rumble?: number;
  shake?: number;
  quality: number;
  showNet: boolean;
  /** DualSense adaptive triggers over WebHID (Chrome/Edge) */
  triggers?: boolean;
}

// Keyboard mirrors the pad grammar: Space jump (again in the air to fly), X hover, Shift sprint,
// C / Ctrl dive roll, right mouse aim.
const KEYS: Record<string, number> = {
  Space: Btn.Jump, KeyR: Btn.Reload, KeyF: Btn.Melee, KeyV: Btn.Melee, KeyC: Btn.Dodge, ControlLeft: Btn.Dodge,
  KeyQ: Btn.Special, KeyE: Btn.Interact, // Shift is sprint on the ground and hover in the air (see key())
  // weapons lane: G grenade, X / wheel swap, T or 3 the special weapon
  KeyG: Btn.Throw, KeyX: Btn.Swap, KeyT: Btn.SpecialWeapon, Digit3: Btn.SpecialWeapon, KeyB: Btn.PowerUp, KeyH: Btn.Drink,
};

/** radial deadzone with an outer ring: direction is kept, magnitude rescaled to 0..1 */
function radial(x: number, y: number, inner: number, outer = 0.97): [number, number, number] {
  const m = Math.hypot(x, y);
  if (m < inner) return [0, 0, 0];
  const k = Math.min(1, (m - inner) / (outer - inner));
  return [(x / m) * k, (y / m) * k, k];
}

export class Input {
  yaw = 0;
  pitch = 0;
  mx = 0;
  my = 0;
  private held = 0;
  private latched = 0;
  private keys = new Set<string>();
  locked = false;
  glyphs: 'kbm' | 'pad' = 'kbm';
  settings: Settings;
  private canvas: HTMLElement;
  /** set by menus: the Pause bit rides on every input frame while true */
  paused = false;
  /** set while the title screen covers the game: no buttons, no movement, no look. Anything held
   *  through it is ignored until released, so the button that dismissed the title doesn't also act. */
  blocked = false;
  onToggleMenu: () => void = () => {};
  onToggleNet: () => void = () => {};
  onTogglePosse: (show: boolean) => void = () => {};
  private padPrev = 0;
  private padFire = false;
  aimHeld = false;
  /** set by the game each frame: Shift means hover in the air, sprint on the ground */
  airborne = false;
  /** traversal flight (movement lane): the camera eases behind the line of flight while the right stick is idle */
  flying = false;
  flightYaw = 0;
  flightPitch = 0;
  private mouseAim = false;
  /** Create toggles the posse panel (Tab is hold-to-show on keyboard) */
  posseOpen = false;
  /** pad buttons held when the game started / resumed: ignored until released, so the ✕ that
   *  rode out doesn't also jump and the ○ that closed the menu doesn't also dodge */
  private padIgnore = -1;
  private rumbleUntil = 0;
  /** ping / quick chat: Z or D-pad up. Tap pings; hold opens the wheel (client/review/comms.ts). */
  pingHeld = false;
  private pingLatch = false;
  /** set by the comms wheel while it's open: the move stick picks a line instead of walking */
  wheelOpen = false;
  wheelX = 0;
  wheelY = 0;
  /** rumble strength 0..1 (pause menu); 0 turns it off */
  rumbleScale = 1;

  constructor(canvas: HTMLElement, settings: Settings) {
    this.canvas = canvas;
    this.settings = settings;
    window.addEventListener('keydown', (e) => this.key(e, true));
    window.addEventListener('keyup', (e) => this.key(e, false));
    window.addEventListener('blur', () => { this.keys.clear(); this.held = 0; });
    canvas.addEventListener('mousedown', (e) => {
      if (this.blocked) return;
      if (!this.locked) { if (!this.paused) this.lock(); return; }
      this.glyphs = 'kbm';
      if (e.button === 0) { this.held |= Btn.Fire; this.latched |= Btn.Fire; }
      if (e.button === 2) this.mouseAim = this.aimHeld = true;
      if (e.button === 1) { this.held |= Btn.Melee; this.latched |= Btn.Melee; }
    });
    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.held &= ~Btn.Fire;
      if (e.button === 2) this.mouseAim = this.aimHeld = false;
      if (e.button === 1) this.held &= ~Btn.Melee;
    });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('wheel', (e) => { if (this.locked && Math.abs(e.deltaY) > 4) { this.latched |= Btn.Swap; e.preventDefault(); } }, { passive: false });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (!this.locked && !this.paused && !this.blocked) this.onToggleMenu(); // Esc out of pointer lock = pause
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      const s = this.settings.sensitivity / 1000 * this.zoomScale;
      this.yaw -= e.movementX * s;
      this.pitch -= e.movementY * s * (this.settings.invertY ? -1 : 1);
      this.pitch = Math.max(-1.4, Math.min(1.4, this.pitch));
    });
  }

  lock() {
    const retry = () => { try { (this.canvas.requestPointerLock() as unknown as Promise<void> | undefined)?.catch?.(() => {}); } catch { /* needs a gesture */ } };
    try { (this.canvas as HTMLCanvasElement).requestPointerLock({ unadjustedMovement: true } as never)?.catch?.(retry); }
    catch { retry(); }
  }
  unlock() { if (document.pointerLockElement) document.exitPointerLock(); }

  private key(e: KeyboardEvent, down: boolean) {
    if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
    if (this.blocked) { if (!down) this.keys.delete(e.code); return; }
    if (e.code === 'F3' && down) { e.preventDefault(); this.onToggleNet(); return; }
    if (e.code === 'Tab') { e.preventDefault(); this.onTogglePosse(down); return; }
    if ((e.code === 'KeyP' || e.code === 'Escape') && down && !e.repeat) {
      if (e.code === 'KeyP' || !this.locked) this.onToggleMenu();
      return;
    }
    this.glyphs = 'kbm';
    if (down && e.code === 'KeyZ') this.pingLatch = true; // a tap shorter than a frame still pings
    if (down) this.keys.add(e.code); else this.keys.delete(e.code);
    const shift = e.code === 'ShiftLeft' || e.code === 'ShiftRight';
    const b = shift ? (this.airborne ? Btn.Hover : Btn.Sprint) : KEYS[e.code];
    if (b) {
      if (down) { if (!e.repeat) this.latched |= b; this.held |= b; }
      else this.held &= shift ? ~(Btn.Hover | Btn.Sprint) : ~b;
      if (this.locked) e.preventDefault();
    }
  }

  /** Called once per render frame: poll pads, integrate stick look. */
  poll(dt: number) {
    if (this.blocked) {
      this.padIgnore = -1; this.padPrev = 0x30000; // Options / Create held through the title don't fire either
      this.mx = this.my = 0; this.lookMag = 0; this.latched = 0; this.padHeld = 0; this.padFire = false;
      return;
    }
    let mx = (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
    let my = (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0);
    const pad = firstPad();
    let padBtns = 0, padAim = false, padPing = false;
    for (const gp of pad ? [pad] : []) {
      // radial deadzones (a square per-axis deadzone snaps diagonals to the axes)
      const [lx, ly] = radial(gp.axes[0] ?? 0, gp.axes[1] ?? 0, 0.12);
      const [rx, ry, rm] = radial(gp.axes[2] ?? 0, gp.axes[3] ?? 0, 0.06);
      const btn = (i: number) => !!gp.buttons[i]?.pressed;
      // PS (16) and touchpad (17) do nothing in play and don't count as pad activity
      const any = Math.abs(lx) + Math.abs(ly) + rm > 0 || gp.buttons.some((b, i) => i < 16 && b.pressed);
      if (any) this.glyphs = 'pad';
      if (lx || ly) { mx = lx; my = -ly; }
      // look: an exponent curve on the stick's MAGNITUDE (precise near centre, fast at the edge, no
      // axis snapping), then a turn boost that ramps in after the stick has been pinned for 0.15 s,
      // so a 180 is quick without making fine aim twitchy
      if (rm > 0.94) this.pinT += dt; else this.pinT = 0;
      this.boost += ((this.pinT > 0.15 ? 1 : 0) - this.boost) * Math.min(1, dt * (this.pinT > 0.15 ? 4 : 12));
      const shaped = Math.pow(rm, 1.6) / (rm || 1); // 25% stick ~17 deg/s, 50% ~70, full 220 before the boost
      const ps = this.settings.padSensitivity * dt * this.lookScale * this.zoomScale;
      const yawBoost = 1 + 0.65 * this.boost;
      this.yaw -= rx * shaped * ps * 3.2 * yawBoost;
      this.pitch -= ry * shaped * ps * 2.3 * (1 + 0.2 * this.boost) * (this.settings.invertY ? -1 : 1);
      this.pitch = Math.max(-1.4, Math.min(1.4, this.pitch));
      if (btn(12)) padPing = true;
      this.lookMag = rm;
      // The Godot game's pad map (scripts/autoload/input_setup.gd; Nick 2026-10-21, James approved): ✕ jump (again
      // airborne: fly, in flight: drop, in a hover: take off), ○ dodge (roll / slide / air dive / barrel roll) and
      // ○○ within 400 ms the class dash, □ reload / revive, △ swap guns, R2 fire, L2 aim, L1 hover (the web's), L3 sprint,
      // R1 grenade, R3 melee, D-pad ↓ power-up, D-pad → whiskey, D-pad ← special weapon. Full map: web/CONTROLS.md
      if (btn(0)) padBtns |= Btn.Jump;
      {
        // ○: the first tap dodges at once; a second tap inside the window is the class dash instead (Godot
        // player_input.gd _handle_dodge_press), and a tap past the window is a fresh first tap
        const circ = btn(1);
        const now = performance.now();
        if (circ && !this.circPrev) {
          if (now - this.circTapAt <= 400) { this.latched |= Btn.Special; this.circTapAt = -1e9; this.circSwallow = true; }
          else this.circTapAt = now;
        }
        if (!circ) this.circSwallow = false;
        this.circPrev = circ;
        if (circ && !this.circSwallow) padBtns |= Btn.Dodge;
      }
      if (btn(2)) padBtns |= Btn.Reload | Btn.Interact;
      if (btn(3)) padBtns |= Btn.Swap;
      if ((gp.buttons[7]?.value ?? 0) > 0.3) padBtns |= Btn.Fire;
      if (btn(11)) padBtns |= Btn.Melee;
      if (btn(5)) padBtns |= Btn.Throw;
      if (btn(14)) padBtns |= Btn.SpecialWeapon; // D-pad ◀ (Godot's spare hover button; hover is L1 here)
      if (btn(13)) padBtns |= Btn.PowerUp; // D-pad ↓: fire the held power-up (Godot use_powerup)
      if (btn(15)) padBtns |= Btn.Drink; // D-pad →: chug a bottle of whiskey (Godot use_stim)
      if (btn(4)) padBtns |= Btn.Hover;
      if (btn(10)) padBtns |= Btn.Sprint;
      padAim = (gp.buttons[6]?.value ?? 0) > 0.3;
      if (btn(9) && !(this.padPrev & 0x10000)) this.onToggleMenu();
      if (btn(8) && !(this.padPrev & 0x20000)) { this.posseOpen = !this.posseOpen; this.onTogglePosse(this.posseOpen); }
      this.padPrev = (btn(9) ? 0x10000 : 0) | (btn(8) ? 0x20000 : 0);
      break;
    }
    if (!pad) this.lookMag = 0;
    if (this.flying && this.lookMag < 0.1 && !this.paused) {
      let dy = this.flightYaw - this.yaw; dy -= Math.round(dy / (Math.PI * 2)) * Math.PI * 2;
      const k = Math.min(1, dt * 5);
      this.yaw += dy * k; // pitch is the control itself: never chased
    }
    if (this.padIgnore === -1) this.padIgnore = padBtns;
    this.padIgnore &= padBtns; // released buttons count again
    padBtns &= ~this.padIgnore;
    this.latched |= padBtns & ~this.padPrevBtns;
    this.padPrevBtns = padBtns;
    this.padHeld = padBtns;
    this.padFire = (padBtns & Btn.Fire) !== 0;
    this.aimHeld = this.mouseAim || padAim;
    this.pingHeld = padPing || this.keys.has('KeyZ') || this.pingLatch;
    this.pingLatch = false;
    if (this.paused) this.pingHeld = false;
    if (this.wheelOpen) { this.wheelX = mx; this.wheelY = my; mx = 0; my = 0; }
    this.mx = mx; this.my = my;
  }
  private padPrevBtns = 0;
  private padHeld = 0;
  /** ○ double-tap state: the last first-tap time, whether ○ was down last poll, and whether this press was eaten as a dash */
  private circTapAt = -1e9;
  private circPrev = false;
  private circSwallow = false;
  private pinT = 0;
  private boost = 0;
  /** right-stick deflection 0..1 this frame (aim assist reads it) */
  lookMag = 0;
  /** aim friction from the game (1 = none): scales stick look only, never the mouse */
  lookScale = 1;
  /** set by the game from the lens: tan(fov/2) / tan(base/2), so ADS turns the same on screen as the hip */
  zoomScale = 1;

  /** ignore pad buttons that are down right now until they're released */
  swallowPad() { this.padIgnore = -1; }

  /** Light rumble on the active pad. Cheap, throttled, never throws (Safari/Firefox may lack it). */
  rumble(strong: number, weak: number, ms: number) {
    if (this.glyphs !== 'pad' || this.paused || this.rumbleScale <= 0) return;
    strong *= this.rumbleScale; weak *= this.rumbleScale;
    const now = performance.now();
    if (now < this.rumbleUntil - ms * 0.5) return; // don't stack effects
    this.rumbleUntil = now + ms;
    try {
      const act = (firstPad() as (Gamepad & { vibrationActuator?: { playEffect?: (t: string, p: object) => Promise<unknown> } }) | null)?.vibrationActuator;
      const pr = act?.playEffect?.('dual-rumble', { startDelay: 0, duration: ms, strongMagnitude: Math.min(1, strong), weakMagnitude: Math.min(1, weak) });
      pr?.catch?.(() => {});
    } catch { /* no haptics */ }
  }

  /** Buttons for the next tick: held | anything pressed since the last tick. */
  takeButtons(): number {
    let b = this.held | this.padHeld | this.latched | (this.aimHeld ? Btn.Aim : 0);
    this.latched = 0;
    if (this.paused) b = Btn.Pause;
    if (this.blocked) b = 0;
    return b;
  }
}
